/**
 * FoodScanResults — V3.0 redesigned post-scan analysis screen.
 *
 * 9 sections (top to bottom):
 *   1. Scanned image + meal header
 *   2. Calorie headline (with daily-goal mini ring)
 *   3. Macro breakdown bar (segmented + 3 macro rows + fibre row)
 *   4. Meal score (calculated client-side from user goal)
 *   5. Micronutrient cards (horizontal scroll, 7 cards)
 *   6. Health insights (3 lines, generated client-side)
 *   7. Goal impact (3 progress rows: calories, protein, goal-specific)
 *   8. Pair It With (2 suggestion pills)
 *   9. Detected items list (existing checklist with portion multipliers)
 *
 * Real-time recalculation: portion multiplier change = pure client arithmetic.
 */
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Check, Camera as CameraIcon, Plus, Pencil, Bookmark } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { useGuestStore, isGuest, trackGuest } from "../../store/guestStore";
import RegistrationSheet from "../../components/common/RegistrationSheet";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const MULTIPLIERS = [0.5, 0.75, 1, 1.5, 2];

const RDA = {
  fibre_g: 30,
  sodium_mg: 2300,
  sugar_g: 50,
  protein_g: 60,
  fats_g: 70,
};

// ──────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────
function applyMultiplier(item) {
  const m = item.multiplier ?? 1;
  return {
    ...item,
    _calories: Math.round((item.calories || 0) * m),
    _protein_g: +((item.protein_g || 0) * m).toFixed(1),
    _carbs_g: +((item.carbs_g || 0) * m).toFixed(1),
    _fats_g: +((item.fats_g || 0) * m).toFixed(1),
    _fibre_g: +((item.fibre_g || 0) * m).toFixed(1),
    _sugar_g: +((item.sugar_g || 0) * m).toFixed(1),
    _sodium_mg: Math.round((item.sodium_mg || 0) * m),
  };
}

function recomputeTotals(items) {
  const checked = items.filter((i) => i.checked);
  const t = {
    calories: 0, protein_g: 0, carbs_g: 0, fats_g: 0,
    fibre_g: 0, sugar_g: 0, sodium_mg: 0, glycemic_load: 0,
  };
  for (const it of checked) {
    const a = applyMultiplier(it);
    t.calories += a._calories;
    t.protein_g += a._protein_g;
    t.carbs_g += a._carbs_g;
    t.fats_g += a._fats_g;
    t.fibre_g += a._fibre_g;
    t.sugar_g += a._sugar_g;
    t.sodium_mg += a._sodium_mg;
    if (it.glycemic_index != null) {
      t.glycemic_load += (it.glycemic_index * a._carbs_g) / 100;
    }
  }
  return {
    calories: Math.round(t.calories),
    protein_g: +t.protein_g.toFixed(1),
    carbs_g: +t.carbs_g.toFixed(1),
    fats_g: +t.fats_g.toFixed(1),
    fibre_g: +t.fibre_g.toFixed(1),
    sugar_g: +t.sugar_g.toFixed(1),
    sodium_mg: Math.round(t.sodium_mg),
    glycemic_load: +t.glycemic_load.toFixed(1),
  };
}

function computeMealScore(totals, goalType) {
  let s = 70;
  const dominantPct = (m, kcalPerG) => {
    const cals = (totals[m] || 0) * kcalPerG;
    return totals.calories ? cals / totals.calories : 0;
  };
  const carbsPct = dominantPct("carbs_g", 4);
  const proteinPct = dominantPct("protein_g", 4);
  const fatsPct = dominantPct("fats_g", 9);

  if (goalType === "lose_weight") {
    if (totals.glycemic_load > 15) s -= 10;
    if (totals.sugar_g > 15) s -= 10;
    if (totals.fibre_g > 5) s += 10;
    if (totals.protein_g > 20) s += 15;
  } else if (goalType === "gain_muscle") {
    if (totals.protein_g > 30) s += 20;
    if (totals.protein_g < 15) s -= 20;
  } else {
    if (carbsPct < 0.6 && proteinPct < 0.6 && fatsPct < 0.6) s += 15;
    if (totals.sodium_mg > 1000) s -= 10;
  }
  return Math.max(0, Math.min(100, s));
}

function scoreLabel(score, totals) {
  if (score >= 90) return "Excellent for your goal";
  if (score >= 75) return "Good choice";
  if (score >= 60) return "Decent — could be better";
  if (score >= 40) {
    const carbsCal = totals.carbs_g * 4;
    const proteinCal = totals.protein_g * 4;
    const fatsCal = totals.fats_g * 9;
    const max = Math.max(carbsCal, proteinCal, fatsCal);
    const dom =
      max === carbsCal ? "carbs" : max === proteinCal ? "protein" : "fats";
    return `High in ${dom}`;
  }
  return "Consider pairing with more protein";
}

function generateInsights(totals, goalType) {
  const insights = [];
  // 1 — best
  const fibrePct = (totals.fibre_g / RDA.fibre_g) * 100;
  const proteinPct = (totals.protein_g / RDA.protein_g) * 100;
  if (fibrePct >= 30) {
    insights.push(
      `Good source of fibre — provides ${Math.round(fibrePct)}% of your daily need.`
    );
  } else if (proteinPct >= 30) {
    insights.push(
      `Good source of protein — provides ${Math.round(proteinPct)}% of your daily need.`
    );
  } else {
    insights.push(`Provides ${totals.calories} calories of mixed nutrition.`);
  }

  // 2 — gap
  if (goalType === "gain_muscle" && totals.protein_g < 25) {
    insights.push(
      "Low in protein for your muscle gain goal. Consider adding an egg or paneer."
    );
  } else if (totals.fibre_g < 5) {
    insights.push(
      "Low in fibre. Adding a small bowl of salad or sprouts would help."
    );
  } else if (totals.fats_g < (totals.calories * 0.15) / 9) {
    insights.push(
      "Low in healthy fats. Adding a small handful of nuts would help."
    );
  } else {
    insights.push("Balanced macros — no major nutritional gap.");
  }

  // 3 — glycemic / energy
  if (totals.glycemic_load > 0) {
    if (totals.glycemic_load < 10)
      insights.push(
        "Low glycemic load — energy from this meal will release steadily."
      );
    else if (totals.glycemic_load <= 20)
      insights.push(
        "Moderate glycemic load — pair with protein to stabilise energy."
      );
    else
      insights.push(
        "High glycemic load — best consumed before a workout for fuel, or pair with fibre and protein."
      );
  } else if (totals.sugar_g > 15) {
    insights.push("High sugar content — best enjoyed occasionally.");
  } else {
    insights.push("Low in sugar — a good everyday choice.");
  }

  return insights;
}

function generatePairings(totals, goalType) {
  const out = [];
  const proteinTarget = goalType === "gain_muscle" ? 30 : 20;
  if (totals.protein_g < proteinTarget * 0.4) {
    out.push({ name: "Boiled egg", why: "Adds protein" });
  }
  if (totals.fibre_g < RDA.fibre_g * 0.3) {
    out.push({ name: "Cucumber salad", why: "Adds fibre" });
  }
  const fatPct = totals.calories ? (totals.fats_g * 9) / totals.calories : 0;
  if (fatPct < 0.15 && out.length < 2) {
    out.push({ name: "Handful of badam", why: "Healthy fats" });
  }
  if (totals.glycemic_load > 15 && out.length < 2) {
    out.push({ name: "1 katori dahi", why: "Balances GI" });
  }
  return out.slice(0, 2);
}

// ──────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────
export default function FoodScanResults({ scan, scannedImage, onDiscard, onSaved }) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const guest = !user && isGuest();
  const guestStore = useGuestStore();

  const [items, setItems] = useState(() =>
    (scan.items || []).map((it) => ({ ...it, checked: true, multiplier: 1 }))
  );
  const [showReg, setShowReg] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [todayLog, setTodayLog] = useState({ calories: 0, protein_g: 0, fibre_g: 0 });
  const [goalInfo, setGoalInfo] = useState(null); // {calorie_goal, protein_goal_g, carbs_goal_g, goal_type, basis}
  const [verdict, setVerdict] = useState(null); // {tag, tag_label, reason, alternative}
  const [gaps, setGaps] = useState([]);
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  const [fixIdx, setFixIdx] = useState(null); // index of item currently being "Fixed"
  const [mealSaved, setMealSaved] = useState(false);

  // Today's existing log totals (for goal impact section)
  useEffect(() => {
    if (!user) return;
    const today = new Date().toISOString().slice(0, 10);
    apiClient
      .get(`/nutrition/logs?date=${today}`)
      .then((r) => {
        const log = (r.data && r.data[0]) || null;
        if (log?.totals) setTodayLog(log.totals);
      })
      .catch(() => {});
  }, [user]);

  // Real BMR/TDEE-based goal — replaces the old (nonexistent) user.daily_calorie_target etc.
  useEffect(() => {
    if (!user) return;
    apiClient
      .get("/users/me/calorie-goal")
      .then((r) => setGoalInfo(r.data))
      .catch(() => {});
  }, [user]);

  const totals = useMemo(() => recomputeTotals(items), [items]);
  const goalType = goalInfo?.goal_type || "maintain"; // lose_weight | gain_muscle | maintain
  const dailyGoal = goalInfo?.calorie_goal || 2200;
  const proteinTarget = goalInfo?.protein_goal_g || (goalType === "gain_muscle" ? 120 : 80);
  const score = computeMealScore(totals, goalType);
  const label = scoreLabel(score, totals);
  const insights = generateInsights(totals, goalType);
  const pairings = generatePairings(totals, goalType);
  const lowConfAny = items.some((i) => i.confidence === "low");

  // Personalized verdict + whole-meal gaps — server-side, real profile
  // (allergies/medical conditions/goal). Re-fires whenever the settled
  // item list changes (initial load, Add Item, Fix name).
  useEffect(() => {
    if (items.length === 0) return;
    const t = setTimeout(() => {
      apiClient
        .post("/nutrition/analyze-meal", { items })
        .then((r) => {
          setVerdict(r.data?.verdict || null);
          setGaps(r.data?.gaps || []);
        })
        .catch(() => {});
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const cycleMultiplier = (idx) => {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        const cur = it.multiplier ?? 1;
        const c = MULTIPLIERS.indexOf(cur);
        const next = MULTIPLIERS[(c + 1) % MULTIPLIERS.length];
        // Iter 51: sync POST-multiplier macros to guestStore so the
        // accumulation summary + 6th-scan gate show doubled values on a
        // 2× portion. `it.calories` etc are the BASE (never mutated after
        // initial setState), so multiply base by the new multiplier.
        if (guest && it.client_scan_id) {
          const baseP = it.protein_g ?? 0;
          const baseC = it.carbs_g ?? 0;
          const baseF = (it.fats_g ?? it.fat_g) ?? 0;
          const baseCal = it.calories ?? 0;
          guestStore.updateScanByClientId(it.client_scan_id, {
            calories: baseCal * next,
            protein_g: baseP * next,
            carbs_g: baseC * next,
            fat_g: baseF * next,
            portion_multiplier: next,
          });
        }
        return { ...it, multiplier: next };
      })
    );
  };
  const toggleItem = (idx) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, checked: !it.checked } : it)));

  // Manual "Add Item" — merges straight into the same items[] used by
  // recomputeTotals()/save(), so it's automatically part of every total,
  // the macro bar, the score, and the /nutrition/log write.
  const addItem = (picked) => {
    setItems((prev) => [
      ...prev,
      {
        name: picked.name,
        portion_description: picked.portion_description,
        portion_grams: picked.portion_grams,
        calories: picked.calories || 0,
        protein_g: picked.protein_g || 0,
        carbs_g: picked.carbs_g || 0,
        fats_g: picked.fat_g ?? picked.fats_g ?? 0,
        fibre_g: picked.fibre_g || 0,
        sugar_g: picked.sugar_g || 0,
        sodium_mg: picked.sodium_mg || 0,
        glycemic_index: picked.glycemic_index ?? null,
        food_group: picked.food_group ?? null,
        checked: true,
        multiplier: 1,
        source: "manual_add",
        confidence: "high",
      },
    ]);
    setAddSheetOpen(false);
  };

  // "Fix name" — replaces a misidentified item's name+macros in place.
  // Flagged with _corrected so save() can teach the correction back.
  const fixItem = (idx, picked) => {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        return {
          ...it,
          _corrected: true,
          _original_name: it._original_name || it.name,
          name: picked.name,
          portion_description: picked.portion_description,
          portion_grams: picked.portion_grams,
          calories: picked.calories || 0,
          protein_g: picked.protein_g || 0,
          carbs_g: picked.carbs_g || 0,
          fats_g: picked.fat_g ?? picked.fats_g ?? 0,
          fibre_g: picked.fibre_g || 0,
          sugar_g: picked.sugar_g || 0,
          sodium_mg: picked.sodium_mg || 0,
          glycemic_index: picked.glycemic_index ?? null,
          food_group: picked.food_group ?? null,
          source: "manual_fix",
          confidence: "high",
        };
      })
    );
    setFixIdx(null);
  };

  const save = async () => {
    if (guest || !user) {
      // Iter 51: prune unticked items from guestStore BEFORE the sheet
      // opens migration. Otherwise a guest who unticks an item still has
      // it in pendingFoodScans, and /guest/migrate would persist it.
      if (guest) {
        const keepIds = items
          .filter((it) => it.checked && it.client_scan_id)
          .map((it) => it.client_scan_id);
        if (keepIds.length > 0) {
          guestStore.pruneScansExcept(keepIds);
        }
      }
      trackGuest("soft_gate_shown", { context: "save_scan" });
      setShowReg(true);
      return;
    }
    const picked = items.filter((it) => it.checked);
    if (picked.length === 0) return toast.error("Tick at least one item");
    try {
      for (const it of picked) {
        const a = applyMultiplier(it);
        await apiClient.post("/nutrition/log", {
          name: it.name,
          calories: a._calories,
          protein_g: a._protein_g,
          carbs_g: a._carbs_g,
          fat_g: a._fats_g,
          portion_multiplier: it.multiplier ?? 1,
          source: it.source || "gemini",
        });
      }
      // Teach any "Fix name" corrections back for next time.
      for (const it of picked) {
        if (!it._corrected || !it._original_name) continue;
        apiClient
          .post("/nutrition/scan-correction", {
            guessed_name: it._original_name,
            corrected_item: {
              name: it.name,
              portion_description: it.portion_description,
              portion_grams: it.portion_grams,
              calories: it.calories,
              protein_g: it.protein_g,
              carbs_g: it.carbs_g,
              fats_g: it.fats_g,
              fibre_g: it.fibre_g,
              sugar_g: it.sugar_g,
              sodium_mg: it.sodium_mg,
              glycemic_index: it.glycemic_index,
              food_group: it.food_group,
            },
          })
          .catch(() => {});
      }
      setSavedFlash(true);
      setTimeout(() => {
        onSaved?.();
      }, 2000);
    } catch {
      toast.error("Could not save meals");
    }
  };

  const saveMealTemplate = async () => {
    if (guest || !user) {
      setShowReg(true);
      return;
    }
    try {
      await apiClient.post("/nutrition/saved-meals", {
        name: scan.meal_name || "Saved meal",
        items: items.map((it) => ({ ...it, _corrected: undefined, _original_name: undefined })),
      });
      setMealSaved(true);
      toast.success("Meal saved — find it under Saved Meals next time");
    } catch {
      toast.error("Could not save this meal");
    }
  };

  const itemCount = items.length;
  const tickedCount = items.filter((i) => i.checked).length;

  return (
    <div data-testid="food-scan-results" className="pb-[88px]">
      {/* ── Section 1 — Image + meal header ── */}
      {scannedImage && (
        <img
          src={scannedImage}
          alt=""
          className="w-full mb-3"
          style={{ height: 200, objectFit: "cover", borderRadius: 16 }}
          data-testid="scan-image"
        />
      )}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h1
            className="font-barlow-cond truncate"
            style={{ fontSize: 26, fontWeight: 900, color: "#1A1A1A", lineHeight: 1.05 }}
            data-testid="meal-name"
          >
            {scan.meal_name || "Your meal"}
          </h1>
          <p className="text-[12px] text-ink-tertiary font-medium mt-0.5">
            {(scan.meal_type_detected ? `${scan.meal_type_detected[0].toUpperCase()}${scan.meal_type_detected.slice(1)} · ` : "")}scanned just now
          </p>
        </div>
        {scan.confidence === "medium" && (
          <ConfBadge label="~Estimated" tone="neutral" />
        )}
        {scan.confidence === "low" && (
          <ConfBadge label="Low confidence" tone="error" />
        )}
      </div>

      {/* ── Section 2 — Calorie headline ── */}
      <Card>
        <div className="flex items-center justify-between gap-3" data-testid="calorie-headline">
          <div className="flex items-baseline gap-1.5">
            <span
              className="font-barlow-cond"
              style={{ fontSize: 56, fontWeight: 900, color: "#1A1A1A", lineHeight: 1 }}
            >
              {totals.calories}
            </span>
            <span
              className="font-barlow-cond"
              style={{ fontSize: 22, fontWeight: 300, color: "#00B0D5" }}
            >
              kcal
            </span>
          </div>
          <DailyGoalRing
            consumed={totals.calories}
            goal={dailyGoal}
            todayConsumed={todayLog.calories}
          />
        </div>
        <div className="text-[12px] text-ink-tertiary mt-1">this meal</div>
      </Card>

      {/* ── Section 3 — Macro breakdown ── */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-barlow-cond text-[18px] font-bold">Macronutrients</h2>
          <span className="text-[12px] text-ink-tertiary font-medium">
            {(totals.protein_g + totals.carbs_g + totals.fats_g).toFixed(1)}g total
          </span>
        </div>
        <MacroBar totals={totals} />
        <div className="mt-3 space-y-2.5" data-testid="macro-rows">
          <MacroRow color="rgba(42,40,37,0.85)" name="Protein" grams={totals.protein_g} pct={pct(totals.protein_g, 4, totals.calories)} />
          <MacroRow color="rgba(42,40,37,0.45)" name="Carbohydrates" grams={totals.carbs_g} pct={pct(totals.carbs_g, 4, totals.calories)} />
          <MacroRow color="rgba(42,40,37,0.20)" name="Fats" grams={totals.fats_g} pct={pct(totals.fats_g, 9, totals.calories)} />
        </div>
        <div className="mt-3 pt-3 border-t border-line2 flex items-center justify-between">
          <span className="text-[14px] text-ink-tertiary">Fibre</span>
          <span className="font-barlow-cond text-[16px] font-extrabold">{totals.fibre_g}g</span>
        </div>
      </Card>

      {/* ── Section 4 — Meal score ── */}
      <Card>
        <div className="flex items-center justify-between" data-testid="meal-score">
          <div>
            <div className="flex items-baseline gap-1">
              <span className="font-barlow-cond" style={{ fontSize: 50, fontWeight: 900, color: "#1A1A1A", lineHeight: 1 }}>{score}</span>
              <span className="font-barlow-cond" style={{ fontSize: 18, fontWeight: 300, color: "#00B0D5" }}>/ 100</span>
            </div>
            <div className="text-[13px] font-semibold text-ink-soft mt-1.5">{label}</div>
          </div>
          <div className="text-right flex flex-col items-end gap-2">
            <div className="text-[11px] text-ink-tertiary font-medium">For your {goalType.replace("_", " ")} goal</div>
            <div className="relative" style={{ width: 6, height: 56, background: "#ECEAE7", borderRadius: 3 }}>
              <div
                className="absolute bottom-0 left-0 right-0"
                style={{
                  height: `${score}%`,
                  background: "#00D4FF",
                  borderRadius: 3,
                  transition: "height 0.3s",
                }}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* ── Section 4.5 — Personalized verdict (good/limit/avoid) ── */}
      {verdict && (
        <Card>
          <div className="flex items-center gap-2" data-testid="meal-verdict">
            <VerdictBadge tag={verdict.tag} label={verdict.tag_label} />
          </div>
          <p className="text-[13px] text-ink-soft mt-2" style={{ lineHeight: 1.5 }}>{verdict.reason}</p>
          {verdict.alternative && (
            <p className="text-[12px] font-medium mt-2" style={{ color: "#00A5C7" }} data-testid="verdict-alternative">
              {verdict.alternative}
            </p>
          )}
        </Card>
      )}

      {/* ── Section 5 — Micronutrient cards ── */}
      <div className="px-1 mb-1.5 flex items-end justify-between">
        <h2 className="font-barlow-cond text-[18px] font-bold">Micronutrients</h2>
        <span className="text-[11px] text-ink-tertiary font-medium">Scroll to see more →</span>
      </div>
      <div
        className="flex gap-2.5 overflow-x-auto pb-2 mb-3 -mx-4 px-4"
        style={{ scrollbarWidth: "none" }}
        data-testid="micros-row"
      >
        <MicroCard label="Sugar" value={totals.sugar_g} unit="g" tone={totals.sugar_g > 15 ? "warn" : "neutral"} />
        <MicroCard label="Sodium" value={totals.sodium_mg} unit="mg" tone={totals.sodium_mg > 600 ? "warn" : "neutral"} />
        <MicroCard label="GI" value={avgGI(items)} unit="GI" tone="neutral" />
        <MicroCard label="GL" value={totals.glycemic_load} unit="GL" tone={totals.glycemic_load > 20 ? "warn" : "neutral"} />
        <MicroCard label="Fibre" value={totals.fibre_g} unit="g" tone={totals.fibre_g >= 5 ? "good" : "neutral"} />
        <MicroCard label="Protein %DV" value={Math.round((totals.protein_g / RDA.protein_g) * 100)} unit="%" tone={(totals.protein_g / RDA.protein_g) >= 0.3 ? "good" : "neutral"} />
        <MicroCard label="Fats %DV" value={Math.round((totals.fats_g / RDA.fats_g) * 100)} unit="%" tone="neutral" />
      </div>

      {/* ── Section 6 — Health insights ── */}
      <Card>
        <h2 className="font-barlow-cond text-[18px] font-bold mb-2">Nutrition Insights</h2>
        {lowConfAny && (
          <p className="text-[11px] text-ink-tertiary mb-2">
            Some items had low confidence. Values are estimates.
          </p>
        )}
        <ul className="space-y-2.5" data-testid="insights">
          {insights.map((i, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span className="mt-1.5 w-[5px] h-[5px] rounded-full shrink-0" style={{ background: "#00B0D5" }} />
              <span className="text-[14px] text-ink-soft" style={{ lineHeight: 1.5 }}>{i}</span>
            </li>
          ))}
          {gaps.map((g, idx) => (
            <li key={`gap-${idx}`} className="flex items-start gap-2" data-testid={`gap-${idx}`}>
              <span className="mt-1.5 w-[5px] h-[5px] rounded-full shrink-0" style={{ background: "#C0392B" }} />
              <span className="text-[14px] text-ink-soft" style={{ lineHeight: 1.5 }}>{g}</span>
            </li>
          ))}
        </ul>
      </Card>

      {/* ── Section 7 — Goal impact ── */}
      <Card>
        <h2 className="font-barlow-cond text-[18px] font-bold mb-3">Today's Progress After This Meal</h2>
        <ProgressRow
          label="Calories"
          consumed={todayLog.calories + totals.calories}
          target={dailyGoal}
          unit="kcal"
        />
        <ProgressRow
          label="Protein"
          consumed={(todayLog.protein_g || 0) + totals.protein_g}
          target={proteinTarget}
          unit="g"
        />
        <ProgressRow
          label={goalType === "lose_weight" ? "Fibre" : goalType === "gain_muscle" ? "Protein boost" : "Carbs"}
          consumed={
            goalType === "lose_weight"
              ? (todayLog.fibre_g || 0) + totals.fibre_g
              : goalType === "gain_muscle"
              ? (todayLog.protein_g || 0) + totals.protein_g
              : totals.carbs_g
          }
          target={
            goalType === "lose_weight"
              ? RDA.fibre_g
              : goalType === "gain_muscle"
              ? proteinTarget
              : 280
          }
          unit="g"
        />
        {goalInfo?.basis === "fallback" && (
          <p className="text-[11px] text-ink-tertiary mt-3">
            Based on estimated daily needs.{" "}
            <button
              onClick={() => navigate("/profile-setup")}
              className="underline"
              data-testid="set-goal-link"
            >
              Set goal
            </button>
          </p>
        )}
      </Card>

      {/* ── Section 8 — Pair It With ── */}
      {pairings.length > 0 && (
        <Card>
          <h2 className="font-barlow-cond text-[18px] font-bold">Pair It With</h2>
          <p className="text-[12px] text-ink-tertiary">To make this meal more complete</p>
          <div className="flex flex-wrap gap-2 mt-3" data-testid="pairings">
            {pairings.map((p, i) => (
              <div
                key={i}
                className="flex items-center gap-2 rounded-pill"
                style={{
                  background: "#ECEAE7",
                  border: "1px solid #E2E0DC",
                  padding: "10px 16px",
                  borderRadius: 100,
                  flex: pairings.length === 1 ? "1 1 100%" : "0 0 auto",
                }}
              >
                <span className="text-[13px] font-semibold">{p.name}</span>
                <span className="text-[11px] text-ink-tertiary">· {p.why}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── Section 9 — Detected items list (existing) ── */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-barlow-cond text-[18px] font-bold">Detected Items</h2>
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-ink-tertiary font-medium">
              {tickedCount} of {itemCount} {itemCount === 1 ? "item" : "items"}
            </span>
            <button
              type="button"
              onClick={() => setAddSheetOpen(true)}
              data-testid="add-item-btn"
              className="flex items-center gap-1 px-2.5 h-7 rounded-pill text-[11px] font-semibold"
              style={{ background: "rgba(0,212,255,0.18)", color: "#1A1A1A", borderRadius: 100 }}
            >
              <Plus size={12} /> Add Item
            </button>
          </div>
        </div>
        <div className="space-y-2.5">
          {items.map((it, i) => {
            const a = applyMultiplier(it);
            return (
              <div key={i} className="flex items-start gap-3" data-testid={`scan-item-${i}`}>
                <button
                  onClick={() => toggleItem(i)}
                  data-testid={`item-toggle-${i}`}
                  aria-label="Toggle"
                  className="mt-1 w-5 h-5 rounded-md grid place-items-center shrink-0"
                  style={{
                    background: it.checked ? "#00D4FF" : "transparent",
                    border: it.checked ? "1px solid #00D4FF" : "1.5px solid #C7C4BF",
                  }}
                >
                  {it.checked && <Check size={12} color="#000" strokeWidth={3} />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[14px]">{it.name}</span>
                    {it.indian_name && (
                      <span className="text-[11px] text-ink-tertiary italic">({it.indian_name})</span>
                    )}
                    {it.source === "indian_db" && (
                      <span
                        className="font-semibold"
                        data-testid={`tag-indian-db-${i}`}
                        style={{
                          background: "rgba(0,255,179,0.18)",
                          color: "#008B6E",
                          fontSize: 9,
                          padding: "2px 8px",
                          borderRadius: 100,
                          letterSpacing: "0.06em",
                        }}
                      >
                        INDIAN DB
                      </span>
                    )}
                    {it.confidence === "low" && (
                      <span
                        style={{
                          background: "#FFF1D1",
                          color: "#A05E00",
                          fontSize: 9,
                          fontWeight: 700,
                          padding: "2px 8px",
                          borderRadius: 100,
                        }}
                      >
                        ~Estimated
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-ink-soft mt-0.5">
                    {it.portion_description || `${it.portion_grams || ""}g`} · {a._calories} kcal
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <button
                      onClick={() => cycleMultiplier(i)}
                      data-testid={`item-multiplier-${i}`}
                      className="px-2.5 h-7 rounded-pill text-[11px] font-semibold"
                      style={{ background: "rgba(0,212,255,0.18)", color: "#1A1A1A", borderRadius: 100 }}
                    >
                      {it.multiplier ?? 1}× portion
                    </button>
                    <button
                      onClick={() => setFixIdx(i)}
                      data-testid={`item-fix-${i}`}
                      aria-label="Not right? Fix name"
                      className="w-7 h-7 rounded-full grid place-items-center shrink-0"
                      style={{ background: "#ECEAE7", border: "1px solid #E2E0DC" }}
                    >
                      <Pencil size={11} color="#4A4845" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* ── Bottom action bar ── */}
      <div
        className="fixed left-0 right-0 bottom-0"
        style={{
          background: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderTop: "1px solid #E2E0DC",
          padding: "12px 16px",
          paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)",
          zIndex: 40,
        }}
        data-testid="results-action-bar"
      >
        {savedFlash ? (
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            data-testid="save-success"
            className="flex items-center justify-center gap-2 mx-auto"
            style={{
              background: "#FFFFFF",
              border: "1px solid #E2E0DC",
              borderRadius: 100,
              height: 48,
              maxWidth: 358,
            }}
          >
            <Check size={18} color="#00D4FF" strokeWidth={3} />
            <span className="font-medium text-[14px]">Saved to Food Log</span>
          </motion.div>
        ) : (
          <div className="flex gap-2.5 max-w-2xl mx-auto">
            <button
              onClick={onDiscard}
              data-testid="discard-scan"
              className="flex-1 rounded-pill border border-line2 text-[14px] font-semibold"
              style={{ height: 48, borderRadius: 100 }}
            >
              Discard
            </button>
            <button
              onClick={saveMealTemplate}
              data-testid="save-meal-template"
              aria-label="Save this meal for next time"
              className="w-12 rounded-pill border border-line2 grid place-items-center shrink-0"
              style={{ height: 48, borderRadius: 100, background: mealSaved ? "rgba(0,212,255,0.18)" : "transparent" }}
            >
              <Bookmark size={16} color="#1A1A1A" fill={mealSaved ? "#1A1A1A" : "none"} />
            </button>
            <button
              onClick={save}
              data-testid="save-to-log"
              className="flex-1 rounded-pill text-[14px] font-bold"
              style={{ height: 48, borderRadius: 100, background: "#00D4FF", color: "#000" }}
            >
              + Save to Food Log
            </button>
          </div>
        )}
      </div>

      {showReg && (
        <RegistrationSheet
          context="save_scan"
          onSuccess={() => {
            setShowReg(false);
            setSavedFlash(true);
            setTimeout(() => onSaved?.(), 2000);
          }}
          onDismiss={() => setShowReg(false)}
        />
      )}

      {addSheetOpen && (
        <AddItemSheet title="Add missed item" onPick={addItem} onClose={() => setAddSheetOpen(false)} />
      )}
      {fixIdx !== null && (
        <AddItemSheet
          title="What is it really?"
          onPick={(picked) => fixItem(fixIdx, picked)}
          onClose={() => setFixIdx(null)}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────
function Card({ children }) {
  return (
    <div
      className="bg-white border border-line2 mb-3"
      style={{ borderRadius: 16, padding: 16 }}
    >
      {children}
    </div>
  );
}

function VerdictBadge({ tag, label }) {
  const tone =
    tag === "good"
      ? { bg: "rgba(0,255,179,0.18)", color: "#008B6E" }
      : tag === "limit"
      ? { bg: "#FFF1D1", color: "#A05E00" }
      : { bg: "rgba(192,57,43,0.15)", color: "#A52121" };
  return (
    <span
      className="font-barlow-cond"
      style={{ fontSize: 16, fontWeight: 800, color: tone.color, background: tone.bg, padding: "6px 14px", borderRadius: 100 }}
    >
      {label}
    </span>
  );
}

// ──────────────────────────────────────────────────────────────
// Add Item / Fix name — shared bottom sheet. Searches the same
// /nutrition/food-search endpoint the Manual tab uses; falls back to
// plain entry fields on a graceful miss.
// ──────────────────────────────────────────────────────────────
function AddItemSheet({ title, onPick, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [gracefulMiss, setGracefulMiss] = useState(false);
  const [manual, setManual] = useState({ name: "", kcal: "", prot: "", carb: "", fat: "", portion: "1" });

  useEffect(() => {
    const needle = q.trim();
    if (!needle) {
      setResults([]);
      setGracefulMiss(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(`/nutrition/food-search?q=${encodeURIComponent(needle)}&limit=8`);
        setResults(data.items || []);
        setGracefulMiss(!!data.graceful_miss && (data.items || []).length === 0);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const pickResult = (item) => {
    const portion = Math.max(0.25, Number(manual.portion) || 1);
    onPick({
      name: item.name,
      portion_description: item.portion_description,
      portion_grams: item.portion_grams,
      calories: (item.calories || 0) * portion,
      protein_g: (item.protein_g || 0) * portion,
      carbs_g: (item.carbs_g || 0) * portion,
      fat_g: (item.fat_g || 0) * portion,
      source: item.source,
    });
  };

  const submitManual = () => {
    if (!manual.name.trim() || !manual.kcal) return;
    const portion = Math.max(0.25, Number(manual.portion) || 1);
    onPick({
      name: manual.name.trim(),
      portion_description: `${portion}×`,
      calories: (Number(manual.kcal) || 0) * portion,
      protein_g: (Number(manual.prot) || 0) * portion,
      carbs_g: (Number(manual.carb) || 0) * portion,
      fat_g: (Number(manual.fat) || 0) * portion,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[80] grid place-items-end"
      style={{ background: "rgba(0,0,0,0.55)" }}
      data-testid="add-item-sheet"
    >
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl mx-auto bg-white rounded-t-[24px] p-5"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 18px)", maxHeight: "88vh", overflowY: "auto" }}
      >
        <h2 className="font-barlow-cond" style={{ fontSize: 22, fontWeight: 800, color: "#1A1A1A" }}>{title}</h2>
        <input
          type="text"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search a food (e.g., pickle, paneer)…"
          data-testid="add-item-search-input"
          className="mt-3 w-full h-11 px-3 rounded-[12px] bg-canvas2 border border-line outline-none text-[14px]"
        />
        <div className="mt-2 flex items-center gap-2">
          <label className="text-[11px] uppercase tracking-[0.08em] font-bold text-ink-tertiary">Quantity</label>
          <input
            type="number"
            inputMode="decimal"
            min="0.25"
            step="0.25"
            value={manual.portion}
            onChange={(e) => setManual((m) => ({ ...m, portion: e.target.value }))}
            data-testid="add-item-quantity"
            className="w-20 h-9 px-2 rounded-[10px] bg-canvas2 border border-line outline-none text-[13px]"
          />
          <span className="text-[11px] text-ink-tertiary">× the default portion (e.g. 0.5 = half katori, 2 = 2 pieces)</span>
        </div>
        {results.length > 0 && (
          <div className="mt-2 border border-line2 rounded-[12px] overflow-hidden" data-testid="add-item-results">
            {results.map((it, idx) => (
              <button
                key={`${it.name}-${idx}`}
                type="button"
                onClick={() => pickResult(it)}
                data-testid={`add-item-result-${idx}`}
                className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-canvas2 border-b border-line2 last:border-b-0"
              >
                <div className="min-w-0">
                  <div className="text-[13px] font-medium capitalize truncate">{it.name}</div>
                  <div className="text-[11px] text-ink-soft">
                    {Math.round(it.calories || 0)} kcal · P {it.protein_g}g · C {it.carbs_g}g · F {it.fat_g}g
                    {it.portion_description ? ` · ${it.portion_description}` : ""}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
        {gracefulMiss && (
          <div className="mt-4 space-y-3">
            <p className="text-[12px] text-ink-tertiary">No match — enter it manually.</p>
            <input
              type="text"
              value={manual.name}
              onChange={(e) => setManual((m) => ({ ...m, name: e.target.value }))}
              placeholder="Food name"
              data-testid="add-item-manual-name"
              className="w-full h-11 px-3 rounded-[12px] bg-canvas2 border border-line outline-none text-[14px]"
            />
            <div className="grid grid-cols-2 gap-2">
              <input type="number" inputMode="decimal" placeholder="Calories" value={manual.kcal}
                onChange={(e) => setManual((m) => ({ ...m, kcal: e.target.value }))}
                data-testid="add-item-manual-kcal"
                className="h-11 px-3 rounded-[12px] bg-canvas2 border border-line outline-none text-[14px]" />
              <input type="number" inputMode="decimal" placeholder="Protein (g)" value={manual.prot}
                onChange={(e) => setManual((m) => ({ ...m, prot: e.target.value }))}
                data-testid="add-item-manual-protein"
                className="h-11 px-3 rounded-[12px] bg-canvas2 border border-line outline-none text-[14px]" />
              <input type="number" inputMode="decimal" placeholder="Carbs (g)" value={manual.carb}
                onChange={(e) => setManual((m) => ({ ...m, carb: e.target.value }))}
                data-testid="add-item-manual-carbs"
                className="h-11 px-3 rounded-[12px] bg-canvas2 border border-line outline-none text-[14px]" />
              <input type="number" inputMode="decimal" placeholder="Fat (g)" value={manual.fat}
                onChange={(e) => setManual((m) => ({ ...m, fat: e.target.value }))}
                data-testid="add-item-manual-fat"
                className="h-11 px-3 rounded-[12px] bg-canvas2 border border-line outline-none text-[14px]" />
            </div>
            <button
              type="button"
              onClick={submitManual}
              disabled={!manual.name.trim() || !manual.kcal}
              data-testid="add-item-manual-submit"
              className="w-full h-11 rounded-pill font-semibold disabled:opacity-50"
              style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}
            >
              Add to meal
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={onClose}
          data-testid="add-item-cancel"
          className="mt-4 w-full h-11 text-[13px] font-medium"
          style={{ color: "#4A4845" }}
        >
          Cancel
        </button>
      </motion.div>
    </motion.div>
  );
}

function ConfBadge({ label, tone }) {
  const color = tone === "error" ? "#A52121" : "#8E8A84";
  return (
    <span
      className="shrink-0 inline-flex items-center"
      data-testid="conf-badge"
      style={{
        background: "#ECEAE7",
        border: "1px solid #E2E0DC",
        fontSize: 10,
        fontWeight: 600,
        color,
        padding: "4px 10px",
        borderRadius: 100,
      }}
    >
      {label}
    </span>
  );
}

function DailyGoalRing({ consumed, goal, todayConsumed = 0 }) {
  const cumulative = todayConsumed + consumed;
  const pctVal = goal ? Math.round((cumulative / goal) * 100) : 0;
  const remaining = goal - cumulative;
  const r = 22;
  const C = 2 * Math.PI * r;
  const offset = C - (Math.min(pctVal, 100) / 100) * C;
  return (
    <div className="text-center" data-testid="daily-goal-ring">
      <svg width={56} height={56} viewBox="0 0 56 56">
        <circle cx={28} cy={28} r={r} fill="none" stroke="#ECEAE7" strokeWidth={4} />
        <circle
          cx={28}
          cy={28}
          r={r}
          fill="none"
          stroke="#00D4FF"
          strokeWidth={4}
          strokeDasharray={C}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 28 28)"
          style={{ transition: "stroke-dashoffset 0.4s" }}
        />
        <text
          x={28}
          y={32}
          textAnchor="middle"
          style={{
            fontFamily: "Barlow Condensed",
            fontSize: 13,
            fontWeight: 800,
            fill: "#1A1A1A",
          }}
        >
          {pctVal}%
        </text>
      </svg>
      <div className="text-[10px] text-ink-tertiary font-medium mt-0.5">of daily goal</div>
      <div
        className="text-[11px] font-semibold mt-0.5"
        style={{ color: remaining < 0 ? "#A52121" : "#6B6760" }}
      >
        {remaining >= 0 ? `${remaining} kcal remaining` : `${Math.abs(remaining)} kcal over goal`}
      </div>
    </div>
  );
}

function MacroBar({ totals }) {
  const calsP = totals.protein_g * 4;
  const calsC = totals.carbs_g * 4;
  const calsF = totals.fats_g * 9;
  const total = calsP + calsC + calsF || 1;
  const wP = (calsP / total) * 100;
  const wC = (calsC / total) * 100;
  const wF = (calsF / total) * 100;
  return (
    <div
      className="flex w-full overflow-hidden"
      style={{ height: 12, borderRadius: 6, background: "#ECEAE7" }}
      data-testid="macro-bar"
    >
      <div style={{ width: `${wP}%`, background: "rgba(42,40,37,0.85)" }} />
      <div style={{ width: `${wC}%`, background: "rgba(42,40,37,0.45)" }} />
      <div style={{ width: `${wF}%`, background: "rgba(42,40,37,0.20)" }} />
    </div>
  );
}

function MacroRow({ color, name, grams, pct }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span style={{ width: 8, height: 8, borderRadius: 4, background: color }} />
        <span className="text-[14px] font-semibold">{name}</span>
      </div>
      <div className="text-right">
        <div className="font-barlow-cond text-[16px] font-extrabold leading-none">{grams}g</div>
        <div className="text-[11px] text-ink-tertiary">{pct}% of calories</div>
      </div>
    </div>
  );
}

function MicroCard({ label, value, unit, tone }) {
  const accent =
    tone === "warn" ? "rgba(192,57,43,0.40)" :
    tone === "good" ? "rgba(42,40,37,0.70)" :
    "rgba(42,40,37,0.15)";
  const display = value == null || isNaN(value) ? "—" : value;
  return (
    <div
      className="shrink-0 relative bg-white border border-line2 text-center"
      style={{ width: 90, height: 90, borderRadius: 14, padding: "12px 8px" }}
      data-testid={`micro-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
    >
      <div
        className="font-barlow-cond"
        style={{ fontSize: 22, fontWeight: 900, color: "#1A1A1A", lineHeight: 1 }}
      >
        {display}
      </div>
      <div className="text-[10px] text-ink-tertiary font-medium mt-0.5">{unit}</div>
      <div
        className="absolute left-0 right-0 bottom-3 mx-auto text-ink-tertiary"
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        className="absolute left-0 right-0 bottom-0"
        style={{ height: 4, background: accent, borderRadius: "0 0 14px 14px" }}
      />
    </div>
  );
}

function ProgressRow({ label, consumed, target, unit }) {
  const p = Math.min((consumed / (target || 1)) * 100, 100);
  const over = consumed > target;
  return (
    <div className="mb-3.5 last:mb-0">
      <div className="flex justify-between items-center mb-1.5">
        <span className="text-[13px] font-semibold">{label}</span>
        <span className="text-[11px] text-ink-tertiary">
          {Math.round(consumed)} / {target} {unit}
        </span>
      </div>
      <div
        className="w-full overflow-hidden"
        style={{ height: 6, background: "#ECEAE7", borderRadius: 3 }}
      >
        <div
          style={{
            width: `${p}%`,
            background: over ? "#A52121" : "#00D4FF",
            height: "100%",
            transition: "width 0.3s",
          }}
        />
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Helpers (continued)
// ──────────────────────────────────────────────────────────────
function pct(grams, kcalPerGram, totalCals) {
  if (!totalCals) return 0;
  return Math.round(((grams * kcalPerGram) / totalCals) * 100);
}
function avgGI(items) {
  const valid = items.filter((i) => i.checked && i.glycemic_index != null);
  if (valid.length === 0) return null;
  return Math.round(valid.reduce((s, i) => s + i.glycemic_index, 0) / valid.length);
}

// ──────────────────────────────────────────────────────────────
// Skeleton — exported for use in FoodScanner during loading
// ──────────────────────────────────────────────────────────────
const ANALYSING_STEPS = [
  "Reading the photo…",
  "Identifying the dish…",
  "Estimating portion size…",
  "Looking up calories & macros…",
  "Cross-checking the Indian food DB…",
  "Almost there — finalising your meal card…",
];

export function FoodScanResultsSkeleton({ scannedImage }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setStepIdx((i) => (i + 1) % ANALYSING_STEPS.length), 1400);
    const tick = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => { clearInterval(interval); clearInterval(tick); };
  }, []);
  return (
    <div data-testid="results-skeleton" className="pb-4">
      {scannedImage && (
        <div style={{ position: "relative", marginBottom: 12 }}>
          <img src={scannedImage} alt=""
            style={{ width: "100%", height: 220, objectFit: "cover", borderRadius: 16, filter: "brightness(0.55)" }} />
          {/* Animated horizontal scanline */}
          <div style={{
            position: "absolute", left: 0, right: 0, top: 0, height: 2,
            background: "linear-gradient(90deg, transparent 0%, #00D4FF 50%, transparent 100%)",
            animation: "scanline 1.4s ease-in-out infinite",
            boxShadow: "0 0 14px rgba(0,212,255,0.85)",
          }} />
          <style>{`
            @keyframes scanline { 0% { top: 0; } 50% { top: calc(100% - 2px); } 100% { top: 0; } }
            @keyframes ai-bounce { 0%,100% { transform: scale(1); opacity: 0.55; } 50% { transform: scale(1.4); opacity: 1; } }
          `}</style>
          {/* Centered analysing pill */}
          <div data-testid="analysing-overlay" style={{
            position: "absolute", inset: 0, display: "flex",
            flexDirection: "column", justifyContent: "center", alignItems: "center",
            color: "white", padding: 24, textAlign: "center",
          }}>
            <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
              {[0, 1, 2].map((i) => (
                <span key={i} style={{
                  width: 10, height: 10, borderRadius: "50%", background: "#00D4FF",
                  animation: `ai-bounce 1s ease-in-out ${i * 0.2}s infinite`,
                }} />
              ))}
            </div>
            <div style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em" }}>
              Analysing your plate…
            </div>
            <div data-testid="analysing-step" style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", marginTop: 6, minHeight: 18 }}>
              {ANALYSING_STEPS[stepIdx]}
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 4 }}>
              {elapsed}s · usually 4-7 seconds
            </div>
          </div>
        </div>
      )}
      {[64, 96, 96, 96].map((h, i) => (
        <div
          key={i}
          className="bg-line2 mb-3 animate-pulse"
          style={{ height: h, borderRadius: 16 }}
        />
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Error state — for insufficient_image_quality / no_food_detected
// ──────────────────────────────────────────────────────────────
export function FoodScanErrorCard({ error, reason, onRetake }) {
  const headline =
    error === "no_food_detected"
      ? "No food found in this photo"
      : "Photo not clear enough";
  const body =
    error === "no_food_detected"
      ? "Try moving closer or improving lighting."
      : reason || "Move closer or try better lighting.";
  return (
    <div
      className="bg-white border border-line2"
      style={{ borderRadius: 16, padding: 20 }}
      data-testid="scan-error-card"
    >
      <div className="font-barlow-cond" style={{ fontSize: 20, fontWeight: 700, color: "#1A1A1A" }}>
        {headline}
      </div>
      <p className="text-[13px] text-ink-soft mt-2">{body}</p>
      <button
        onClick={onRetake}
        data-testid="retake-photo"
        className="mt-4 w-full rounded-pill flex items-center justify-center gap-2"
        style={{ height: 48, background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
      >
        <CameraIcon size={16} /> Retake photo
      </button>
    </div>
  );
}
